import React, { useEffect, useState, useRef } from "react";
import PageHeader from "@/components/PageHeader";
import { adminApi, api, setAdminToken, getAdminToken, adminUpload, fileUrl, BROCHURE_URL } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Image as ImageIcon, Upload, FileText, Download } from "lucide-react";
import Seo from "@/components/Seo";
import ProductsAdmin from "@/pages/admin/ProductsAdmin";
import TestimonialsAdmin from "@/pages/admin/TestimonialsAdmin";

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { password });
      setAdminToken(res.data.token);
      toast.success("Welcome, admin");
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="admin-login-section">
      <div className="industrial-card p-8">
        <div className="eyebrow">Admin</div>
        <h2 className="font-display text-2xl uppercase mt-3 tracking-tight">
          Sign in to dashboard
        </h2>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <label className="label-mono">Password</label>
            <input
              type="password"
              className="vps-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              required
              data-testid="admin-password-input"
            />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="admin-login-submit">
            {loading ? "Verifying…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function NewUpdateForm({ onCreated }) {
  const [form, setForm] = useState({
    title: "",
    category: "Solar Street Lights",
    summary: "",
    content: "",
    image_url: "",
    location: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUpload("/admin/upload-image", file);
      update("image_url", res.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.post("/updates", form);
      toast.success("Update published");
      setForm({
        title: "",
        category: "Solar Street Lights",
        summary: "",
        content: "",
        image_url: "",
        location: "",
      });
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to publish");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="industrial-card p-6 md:p-8 space-y-5" data-testid="admin-new-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl uppercase tracking-tight">
          New project update
        </h3>
        <Plus size={18} className="text-[#e63946]" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className="label-mono">Title *</label>
          <input className="vps-input" value={form.title} onChange={(e) => update("title", e.target.value)} required data-testid="admin-input-title" />
        </div>
        <div>
          <label className="label-mono">Category *</label>
          <select className="vps-input" value={form.category} onChange={(e) => update("category", e.target.value)} required data-testid="admin-input-category">
            {[
              "Solar Street Lights",
              "Lithium Batteries",
              "EV Battery Manufacturing",
              "Electric Vehicles",
              "CSR Projects",
              "Inverters",
              "Pole Fabrication",
              "Student Projects",
              "Other",
            ].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-mono">Location</label>
          <input className="vps-input" value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="e.g. Cherlapally, Hyderabad" data-testid="admin-input-location" />
        </div>
        <div className="md:col-span-2">
          <label className="label-mono">Summary *</label>
          <input className="vps-input" value={form.summary} onChange={(e) => update("summary", e.target.value)} required maxLength={500} data-testid="admin-input-summary" />
        </div>
        <div className="md:col-span-2">
          <label className="label-mono">Image — upload from device OR paste URL</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleImagePick}
              className="hidden"
              data-testid="admin-input-image-file"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-secondary"
              data-testid="admin-image-upload-btn"
            >
              <Upload size={14} />
              {uploading ? "Uploading…" : "Upload image"}
            </button>
            <div className="flex-1 flex items-center gap-2">
              <ImageIcon size={14} className="text-zinc-500" />
              <input
                className="vps-input"
                value={form.image_url}
                onChange={(e) => update("image_url", e.target.value)}
                placeholder="…or paste image URL"
                data-testid="admin-input-image"
              />
            </div>
          </div>
          {form.image_url && (
            <div className="mt-3">
              <img
                src={fileUrl(form.image_url)}
                alt="preview"
                className="h-24 object-cover border border-white/10"
                data-testid="admin-image-preview"
              />
            </div>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="label-mono">Content *</label>
          <textarea className="vps-input" rows={6} value={form.content} onChange={(e) => update("content", e.target.value)} required data-testid="admin-input-content" />
        </div>
      </div>
      <button type="submit" disabled={saving} className="btn-primary" data-testid="admin-submit-btn">
        {saving ? "Publishing…" : "Publish update"}
      </button>
    </form>
  );
}

function BrochureUploader() {
  const [status, setStatus] = useState({ available: false });
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const refresh = () => {
    api
      .get("/brochure/status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    refresh();
  }, []);

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Please choose a PDF file");
      return;
    }
    setUploading(true);
    try {
      await adminUpload("/admin/upload-brochure", file);
      toast.success("Brochure uploaded");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="industrial-card p-6 md:p-8" data-testid="admin-brochure-section">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl uppercase tracking-tight">
          Product brochure (PDF)
        </h3>
        <FileText size={18} className="text-[#e63946]" />
      </div>
      <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
        Upload a PDF brochure. Once uploaded, a Download CTA appears on the Home
        and Services pages.
      </p>
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          onChange={handlePick}
          className="hidden"
          data-testid="admin-brochure-file"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn-primary"
          data-testid="admin-brochure-upload-btn"
        >
          <Upload size={14} />
          {uploading
            ? "Uploading…"
            : status.available
              ? "Replace brochure"
              : "Upload brochure"}
        </button>
        {status.available && (
          <a
            href={BROCHURE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
            data-testid="admin-brochure-download-btn"
          >
            <Download size={14} /> Download current
          </a>
        )}
      </div>
      <div className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500" data-testid="admin-brochure-status">
        Status · {status.available
          ? `Available (${Math.round((status.size || 0) / 1024)} KB)`
          : "Not uploaded yet"}
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [updates, setUpdates] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [tab, setTab] = useState("updates");

  const loadAll = async () => {
    try {
      const [u, e] = await Promise.all([
        adminApi.get("/updates"),
        adminApi.get("/enquiries"),
      ]);
      setUpdates(u.data || []);
      setEnquiries(e.data || []);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    const verify = async () => {
      const token = getAdminToken();
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        await adminApi.get("/admin/verify");
        setAuthed(true);
        await loadAll();
      } catch {
        setAdminToken(null);
      } finally {
        setChecking(false);
      }
    };
    verify();
  }, []);

  const handleLogout = () => {
    setAdminToken(null);
    setAuthed(false);
    toast.success("Signed out");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this update?")) return;
    try {
      await adminApi.delete(`/updates/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  if (checking) {
    return <div className="py-32 text-center text-zinc-400" data-testid="admin-loading">Loading…</div>;
  }

  if (!authed) {
    return (
      <div data-testid="page-admin">
        <Seo path="/admin" title="Admin · Sign in" />
        <PageHeader eyebrow="Admin" title="Dashboard access" subtitle="For VPS team only." testId="admin-page-header" />
        <AdminLogin onLogin={async () => { setAuthed(true); await loadAll(); }} />
      </div>
    );
  }

  return (
    <div data-testid="page-admin">
      <Seo path="/admin" title="Admin Dashboard" />
      <PageHeader eyebrow="Admin" title="Dashboard" subtitle="Manage project updates and view incoming enquiries." testId="admin-page-header" />
      <section className="py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <div className="flex gap-2 flex-wrap" data-testid="admin-tabs">
            {[
              { id: "updates", label: `Updates (${updates.length})` },
              { id: "products", label: "Products" },
              { id: "testimonials", label: "Testimonials" },
              { id: "enquiries", label: `Enquiries (${enquiries.length})` },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                  tab === t.id
                    ? "border-[#e63946] text-[#e63946] bg-[#e63946]/5"
                    : "border-white/15 text-zinc-400 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button onClick={handleLogout} className="btn-secondary" data-testid="admin-logout-btn">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-10">
          {tab === "updates" && (
            <>
              <NewUpdateForm onCreated={loadAll} />
              <BrochureUploader />
              <div>
                <h3 className="font-display text-xl uppercase tracking-tight mb-5">
                  Published updates
                </h3>
                {updates.length === 0 ? (
                  <div className="text-zinc-500 text-sm" data-testid="admin-updates-empty">No updates yet.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {updates.map((u) => (
                      <div key={u.id} className="industrial-card p-5" data-testid={`admin-update-${u.id}`}>
                        {u.image_url && (
                          <img src={fileUrl(u.image_url)} alt={u.title} className="w-full h-32 object-cover border border-white/10 mb-3" />
                        )}
                        <div className="tag-chip tag-chip-red">{u.category}</div>
                        <div className="font-display text-base uppercase mt-3 leading-tight">
                          {u.title}
                        </div>
                        <p className="text-xs text-zinc-400 mt-2 leading-relaxed line-clamp-3">{u.summary}</p>
                        <button
                          onClick={() => handleDelete(u.id)}
                          className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 hover:text-[#e63946] inline-flex items-center gap-1"
                          data-testid={`admin-delete-${u.id}`}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {tab === "products" && <ProductsAdmin />}
          {tab === "testimonials" && <TestimonialsAdmin />}

          {tab === "enquiries" && (
            <div>
              <h3 className="font-display text-xl uppercase tracking-tight mb-5">
                Enquiries
              </h3>
              {enquiries.length === 0 ? (
                <div className="text-zinc-500 text-sm" data-testid="admin-enquiries-empty">No enquiries yet.</div>
              ) : (
                <div className="space-y-3">
                  {enquiries.map((e) => (
                    <div key={e.id} className="industrial-card p-5" data-testid={`admin-enquiry-${e.id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-display text-base">{e.name}</div>
                          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                            {e.email} · {e.phone}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {e.service && <span className="tag-chip tag-chip-red">{e.service}</span>}
                          {e.email_sent ? (
                            <span className="tag-chip" style={{ borderColor: "#25d36655", color: "#25d366" }}>Emailed</span>
                          ) : (
                            <span className="tag-chip">DB only</span>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-zinc-300 mt-4 leading-relaxed whitespace-pre-line">
                        {e.message}
                      </p>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-3">
                        {new Date(e.created_at).toLocaleString("en-IN")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
